import React, { useState } from 'react';
import './NdcOverridesList.scss';
import { Button, Form, Table } from 'react-bootstrap';
import DatePicker from 'react-date-picker';
import { FormikHelpers, useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import useNdcWhitelistSetup from '../../Hooks/useNdcWhitelistSetup';
import Loading from '../../components/LoadingPage/Loading';
import { toast } from 'react-toastify';

const initialFormValues = {
    athenaId: '',
    userName: '',
    fromDate: moment().subtract(7, 'days').format('L'),
    toDate: moment().format('L'),
    active: true,
};

const NdcOverridesList: React.FC = () => {
    const [overrideList, setOverrideList] = useState<any[]>([]);
    const [hasSearched, setHasSearched] = useState<boolean>(false);

    const { getNdcOverrideListAPI, getNdcOverrideListLoading } = useNdcWhitelistSetup();

    const fetchOverrideList = async (filterValues: any) => {
        try {
            const params: Record<string, any> = {};
            if (filterValues.athenaId) params.athenaId = filterValues.athenaId;
            if (filterValues.userName) params.userName = filterValues.userName;
            if (filterValues.fromDate) params.fromDate = moment(filterValues.fromDate).format('YYYY-MM-DDTHH:mm:ss');
            if (filterValues.toDate) params.toDate = moment(filterValues.toDate).format('YYYY-MM-DDTHH:mm:ss');
            params.active = filterValues.active;

            const res = await getNdcOverrideListAPI(params);
            setOverrideList(res || []);
            setHasSearched(true);
        } catch (error: any) {
            console.error("Error while fetching NDC override list: ", error);
            toast.error(error?.message || "Failed to fetch NDC override list.");
        }
    };

    const { handleChange, values, handleSubmit, resetForm, setFieldValue } =
        useFormik({
            initialValues: initialFormValues,
            validationSchema: Yup.object({}),
            onSubmit: async (values: any, { setSubmitting }: FormikHelpers<any>) => {
                try {
                    await fetchOverrideList(values);
                } catch (err: any) { }
                setSubmitting(false);
            },
            enableReinitialize: true,
        });

    const isFilterChanged = () => !isEqual(values, initialFormValues);

    const handleClear = () => {
        if (!isFilterChanged()) return;
        resetForm();
        fetchOverrideList(initialFormValues);
    };

    const formatDate = (date: any) => {
        if (!date) return "-";
        return moment(date).format("MM/DD/YYYY hh:mm:ss A");
    };

    const getAllowedNdcListText = (overrideListItem: any) => {
        if (overrideListItem?.allowedNDCList) {
            if (overrideListItem.allowedNDCList.includes(",")) {
                return `${overrideListItem.allowedNDCList.split(",")[0]}...`;
            }
            return overrideListItem.allowedNDCList;
        }
        return "-";
    };

    return (
        <div className='ndc-override-list-main'>
            {getNdcOverrideListLoading && <Loading />}
            <div className="page-title">
                <h2>NDC Overrides List</h2>
            </div>
            <Form className='ndc-override-list-main__filter-form' onSubmit={handleSubmit}>
                <Form.Group className='form-group'>
                    <Form.Label htmlFor='athena_id'>Athena Id</Form.Label>
                    <Form.Control
                        id='athena_id'
                        name='athenaId'
                        placeholder='Athena Id'
                        value={values.athenaId}
                        onChange={handleChange}
                    />
                </Form.Group>
                <Form.Group className='form-group'>
                    <Form.Label htmlFor='athena_user_id'>Athena User Id</Form.Label>
                    <Form.Control
                        id='athena_user_id'
                        name='userName'
                        placeholder='Athena User Id'
                        value={values.userName}
                        onChange={handleChange}
                    />
                </Form.Group>
                <Form.Group className='form-group'>
                    <Form.Label htmlFor='from_date'>From Date</Form.Label>
                    <DatePicker
                        locale="en-US"
                        format="MM/dd/yyyy"
                        name='fromDate'
                        value={values.fromDate}
                        maxDate={values.toDate ? new Date(values.toDate) : undefined}
                        openCalendarOnFocus={false}
                        showLeadingZeros={true}
                        dayPlaceholder="dd"
                        monthPlaceholder="mm"
                        yearPlaceholder="yyyy"
                        clearIcon={null}
                        calendarIcon={<i className="bi bi-calendar"></i>}
                        className="form-control"
                        onChange={(date: any) => {
                            handleChange({
                                target: {
                                    name: "fromDate",
                                    value: moment(date).isValid() ? moment(date).format("L") : "",
                                },
                            });
                        }}
                    />
                </Form.Group>
                <Form.Group className='form-group'>
                    <Form.Label htmlFor='to_date'>Last Date</Form.Label>
                    <DatePicker
                        locale="en-US"
                        format="MM/dd/yyyy"
                        name='toDate'
                        value={values.toDate}
                        minDate={values.fromDate ? new Date(values.fromDate) : undefined}
                        openCalendarOnFocus={false}
                        showLeadingZeros={true}
                        dayPlaceholder="dd"
                        monthPlaceholder="mm"
                        yearPlaceholder="yyyy"
                        clearIcon={null}
                        calendarIcon={<i className="bi bi-calendar"></i>}
                        className="form-control"
                        onChange={(date: any) => {
                            handleChange({
                                target: {
                                    name: "toDate",
                                    value: moment(date).isValid() ? moment(date).format("L") : "",
                                },
                            });
                        }}
                    />
                </Form.Group>
                <Form.Group className='form-group__check'>
                    <Form.Check
                        inline
                        name='active'
                        id='is_active'
                        label="Active"
                        checked={values.active}
                        onChange={(e) => {
                            setFieldValue("active", e.target.checked);
                        }}
                    />
                </Form.Group>
                <Form.Group className='form-actions'>
                    <Button type='submit' variant='primary'>View</Button>
                    <Button type='button' variant='link' onClick={handleClear}>Clear</Button>
                </Form.Group>
            </Form>

            {hasSearched && (
                <div className="ndc-override-list-main__table-wrapper">
                    <Table responsive hover={true}>
                        <thead>
                            <tr>
                                <th>Athena User Id</th>
                                <th>Timestamp</th>
                                <th className="text-center">Athena Id</th>
                                <th>Name</th>
                                <th className="text-center">Order Type Id</th>
                                <th>Allowed NDC List</th>
                                <th>NDC Code</th>
                                <th className="text-center">Verified</th>
                            </tr>
                        </thead>
                        <tbody>
                            {overrideList?.length > 0 ? (
                                overrideList.map((item, index) => (
                                    <tr key={item?.id || index}>
                                        <td title={item?.athenaUserId || ""}>{item?.athenaUserId || "-"}</td>
                                        <td title={formatDate(item?.timestamp)}>{formatDate(item?.timestamp)}</td>
                                        <td title={item?.athenaId || ""} className="text-center">{item?.athenaId || "-"}</td>
                                        <td title={item?.athenaOrderNDCWhiteList?.athenaOrderMaster?.name || ""}>{item?.athenaOrderNDCWhiteList?.athenaOrderMaster?.name || "-"}</td>
                                        <td title={item?.athenaOrderNDCWhiteList?.athenaOrderMaster?.orderTypeId || ""} className="text-center">{item?.athenaOrderNDCWhiteList?.athenaOrderMaster?.orderTypeId || "-"}</td>
                                        <td title={item?.allowedNDCList || ""}>{getAllowedNdcListText(item)}</td>
                                        <td title={item?.athenaOrderNDCWhiteList?.ndcCode || ""}>{item?.athenaOrderNDCWhiteList?.ndcCode || "-"}</td>
                                        <td title={item?.athenaOrderNDCWhiteList?.isVerified ? "Yes" : "No"} className="text-center">{item?.athenaOrderNDCWhiteList?.isVerified ? "Yes" : "No"}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={8} className="text-center">
                                        No records found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    )
}

export default NdcOverridesList;